import * as React from 'react';
import { Avatar, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import User from 'benchmarking-domain/user';
import { DataGridArrayColumnSortComparator, DataGridInMemory } from 'components/core/shared/data-grid-in-memory';
import { nameOf } from 'helpers/nameof';
import { useNavigate } from 'react-router-dom';

import { paths } from '../../../paths';

interface UsersTableProps {
  rows?: User[];
}

export function UsersTable({ rows = [] }: UsersTableProps): React.JSX.Element {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: nameOf<User>('name'),
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (props: GridRenderCellParams<User, string>) => (
        <Stack sx={{ alignItems: 'center' }} direction="row" spacing={2}>
          <Avatar src={props.row.avatar} />
          <Typography variant="subtitle2">{props.row.name}</Typography>
        </Stack>
      ),
    },
    {
      field: nameOf<User>('email'),
      headerName: 'Email',
      flex: 1,
      minWidth: 250,
    },
    {
      field: nameOf<User>('organisations'),
      headerName: 'Organisations',
      flex: 1,
      minWidth: 250,
      sortComparator: DataGridArrayColumnSortComparator,
      renderCell: (params: GridRenderCellParams<User, string[]>) => (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1, padding: 1 }}>
          {params.value?.map((org) => <Chip key={org} color="default" label={org} size="small" />)}
        </Box>
      ),
    },
    {
      field: nameOf<User>('roles'),
      headerName: 'System roles',
      minWidth: 150,
      sortComparator: DataGridArrayColumnSortComparator,
      renderCell: (params: GridRenderCellParams<User, string[]>) => (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1, padding: 1 }}>
          {params.value?.map((role) => <Chip key={role} color="default" label={role} size="small" />)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<User>) => {
        return (
          <Stack direction="row" spacing={1} sx={{ marginY: 1 }}>
            <Button
              variant="contained"
              onClick={() => {
                navigate(paths.app.admin.users.getDetailsUrl(params.row.id));
              }}
            >
              View&nbsp;user
            </Button>
          </Stack>
        );
      },
    },
  ];

  return <DataGridInMemory columns={columns} rows={rows} searchPlaceholder="Search users" />;
}
